import type { Comparison, Environment } from '@fleequid-secrets/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { Icon } from '@/components/Icon';
import { groupColor, groupLetter, missingCells, populated, riskyCells, summarize } from '@/lib/compare';
import { plural } from '@/lib/format';
import { useOutside } from '@/lib/hooks';
import { navigate, setParam, useSearch } from '@/lib/router';
import { vaultPath } from '@/lib/routes';
import { Cancelled, guarded, useShell } from '@/lib/shell';

// COMPARE: one section across every environment. Which keys each has (a missing one is a deploy waiting to fail), and
// which hold the same value (right for a feature flag, a risk for a production secret). Values never leave the server.
export function Compare(): ReactNode {
    const { environments } = useShell();
    const search = useSearch();
    const [onlyMissing, setOnlyMissing] = useState(false);
    const choices = useMemo(() => {
        const bySlug = new Map<string, { slug: string; name: string; count: number }>();
        for (const environment of environments) {
            for (const section of environment.sections) {
                const entry = bySlug.get(section.slug) ?? { slug: section.slug, name: section.name, count: 0 };
                entry.count += 1;
                bySlug.set(section.slug, entry);
            }
        }

        return [...bySlug.values()];
    }, [environments]);
    const asked = new URLSearchParams(search).get('section');
    const slug = choices.some((choice) => choice.slug === asked) ? asked : (choices[0]?.slug ?? null);
    const comparison = useQuery({ queryKey: ['compare', slug], queryFn: () => api.compare(slug as string), enabled: slug !== null });
    const data = comparison.data && comparison.data.slug === slug ? comparison.data : null;
    const summary = data ? summarize(data) : null;

    if (choices.length === 0) {
        return (
            <main className="page page-scroll">
                <div className="placeholder">
                    <h2>Nothing to compare yet</h2>
                    <p>Once a section exists in two environments (Backend in staging and production), it shows up here side by side.</p>
                </div>
            </main>
        );
    }

    return (
        <main className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Compare</h1>
                    <p>
                        The same section in every environment: which keys each has, and which share a value. The values themselves never leave the
                        server here.
                    </p>
                </div>
                <div className="side">
                    {choices.length <= 6 ? (
                        <div className="seg">
                            {choices.map((choice) => (
                                <button
                                    key={choice.slug}
                                    className={choice.slug === slug ? 'on' : ''}
                                    onClick={() => setParam('section', choice.slug)}
                                >
                                    {choice.name}
                                    <span className="count">{choice.count}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <select className="field" value={slug ?? ''} onChange={(event) => setParam('section', event.target.value)}>
                            {choices.map((choice) => (
                                <option key={choice.slug} value={choice.slug}>
                                    {choice.name} · {plural(choice.count, 'environment')}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
            {summary && (
                <div className="tiles">
                    <div className="tile">
                        <b>{summary.keys}</b>
                        <span>
                            keys across {plural(data?.environments.filter((environment) => environment.sectionId).length ?? 0, 'environment')}
                        </span>
                    </div>
                    <div className="tile">
                        <b className={summary.missing ? 'bad' : ''}>{summary.missing}</b>
                        <span>{summary.missing === 1 ? 'key missing where the section is' : 'keys missing where the section is'}</span>
                    </div>
                    <div className="tile">
                        <b className={summary.risky ? 'warn' : ''}>{summary.risky}</b>
                        <span>
                            {summary.risky === 1
                                ? 'protected value shared with an unprotected environment'
                                : 'protected values shared with unprotected environments'}
                        </span>
                    </div>
                </div>
            )}
            {comparison.isError && <div className="empty">{messageOf(comparison.error)}</div>}
            {data && summary && (
                <div className="panel">
                    <div className="panel-head">
                        <span className="title">{data.name}</span>
                        <span className="note">{plural(data.rows.length, 'key')}</span>
                        <span className="spacer" />
                        <button className={`toggle ${onlyMissing ? 'on' : ''}`} onClick={() => setOnlyMissing(!onlyMissing)}>
                            <span className="sw" />
                            Only keys missing somewhere
                        </button>
                    </div>
                    <Matrix comparison={data} missingByEnvironment={summary.missingByEnvironment} onlyMissing={onlyMissing} />
                    <div className="legend">
                        <span>
                            <span className="cell" style={{ '--g': groupColor(0) } as CSSProperties}>
                                <i>A</i>
                            </span>
                            <span className="cell" style={{ '--g': groupColor(1) } as CSSProperties}>
                                <i>B</i>
                            </span>
                            the same letter, the same value
                        </span>
                        <span>
                            <span className="cell missing">missing</span> where the section has no such key
                        </span>
                        <span>
                            <span className="cell risk" style={{ '--g': groupColor(0) } as CSSProperties}>
                                <i>A</i>
                                <Icon name="warning" size={12} className="riskmark" />
                            </span>
                            a protected value also used outside protection
                        </span>
                    </div>
                </div>
            )}
        </main>
    );
}

function Matrix(props: { comparison: Comparison; missingByEnvironment: number[]; onlyMissing: boolean }): ReactNode {
    const { comparison } = props;
    const filled = populated(comparison);
    const rows = props.onlyMissing ? comparison.rows.filter((row) => missingCells(row, filled).some(Boolean)) : comparison.rows;

    return (
        <div className="matrix-wrap">
            <table className="matrix">
                <thead>
                    <tr>
                        <th className="key">Key</th>
                        {comparison.environments.map((environment, index) => (
                            <th key={environment.id}>
                                <EnvironmentHead comparison={comparison} index={index} missing={props.missingByEnvironment[index] ?? 0} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const risky = riskyCells(row, comparison.environments);
                        const missing = missingCells(row, filled);

                        return (
                            <tr key={row.key}>
                                <td className="key">{row.key}</td>
                                {row.cells.map((cell, index) => {
                                    const environment = comparison.environments[index] as Comparison['environments'][number];
                                    if (!cell) {
                                        return <td key={environment.id}>{missing[index] ? <span className="cell missing">missing</span> : null}</td>;
                                    }

                                    return (
                                        <td key={environment.id}>
                                            <button
                                                className={`cell ${cell.empty ? 'empty' : ''} ${risky[index] ? 'risk' : ''}`}
                                                style={{ '--g': groupColor(cell.group) } as CSSProperties}
                                                title={
                                                    cell.empty
                                                        ? `Empty in ${environment.name}`
                                                        : risky[index]
                                                          ? `The same value as an environment on the other side of protection`
                                                          : `Open it in ${environment.name}`
                                                }
                                                onClick={() => navigate(vaultPath(environment.slug, comparison.slug, row.key))}
                                            >
                                                <i>{cell.empty ? '∅' : groupLetter(cell.group)}</i>
                                                {cell.empty && 'empty'}
                                                {risky[index] && <Icon name="warning" size={12} className="riskmark" />}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={comparison.environments.length + 1} className="empty">
                                {props.onlyMissing ? 'Every environment that has the section has every key.' : 'No keys yet.'}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// An environment's column: its name, how many keys it has, and the way to add the ones it lacks from another.
function EnvironmentHead(props: { comparison: Comparison; index: number; missing: number }): ReactNode {
    const shell = useShell();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const root = useOutside(open, () => setOpen(false));
    const environment = props.comparison.environments[props.index] as Comparison['environments'][number];
    const present = props.comparison.rows.filter((row) => row.cells[props.index]).length;
    const sources = props.comparison.environments.filter((other) => other.id !== environment.id && other.sectionId);
    const target = shell.environments.find((item) => item.id === environment.id) as Environment | undefined;
    const fill = (source: Comparison['environments'][number]): void => {
        setOpen(false);
        if (!source.sectionId || !target) {
            return;
        }
        guarded(shell, target, `copy the keys ${environment.name} lacks from ${source.name}`, (confirm) =>
            api.copySection(source.sectionId as string, environment.id, false, confirm),
        )
            .then(async (outcome) => {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['compare'] }),
                    queryClient.invalidateQueries({ queryKey: ['environments'] }),
                    queryClient.invalidateQueries({ queryKey: ['keys'] }),
                ]);
                shell.say(`${plural(outcome.added, 'key')} added to ${environment.name}, empty: fill them there`, {
                    label: 'Open',
                    run: () => navigate(vaultPath(environment.slug, props.comparison.slug)),
                });
            })
            .catch((error: unknown) => !(error instanceof Cancelled) && shell.say(messageOf(error)));
    };

    return (
        <div className="envhead envpick" ref={root}>
            <i className="dot halo" style={{ '--c': environment.color } as CSSProperties} />
            {environment.sectionId ? (
                <span>{environment.name}</span>
            ) : (
                <span className="absent-section" title={`${environment.name} has no ${props.comparison.name}`}>
                    {environment.name}
                </span>
            )}
            {environment.protected && <Icon name="lock" size={12} />}
            <span className="count">{environment.sectionId ? (present > 0 ? present : 'empty') : '—'}</span>
            {(props.missing > 0 || present === 0) && sources.length > 0 && (
                <button className="iconbtn" title={`Add the keys ${environment.name} lacks, empty`} onClick={() => setOpen(!open)}>
                    <Icon name="plus" size={14} />
                </button>
            )}
            {open && (
                <div className="menu float">
                    <div className="mlabel">Add the missing keys, empty, from</div>
                    {sources.map((source) => (
                        <button key={source.id} onClick={() => fill(source)}>
                            <i className="mdot" style={{ background: source.color }} />
                            {source.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
